import { MentionsFilters, MentionsGrid } from '@/components/mentions';
import { TabContent, TopNavWithTabs } from '@/components/scaffold/top-nav';
import { PageHeader } from '@/components/ui';
import { useMarkMentionsSeen } from '@/hooks/actions/use-mark-mentions-seen';
import { useFlattenedMentions, useMentionsQuery } from '@/hooks/queries/use-mentions-query';
import { useSearchCreatorsQuery } from '@/hooks/queries/use-search-creators-query';
import { useUnseenMentionsCountQuery } from '@/hooks/queries/use-unseen-mentions-count-query';
import { useMentionNavigatorSync } from '@/hooks/use-mention-navigator-sync';
import { usePageTitleStore } from '@/stores/page-title';
import { parseAsString, useQueryState } from 'nuqs';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CreatorsTable from '../creators/Creators.Table';

const MentionsPage = () => {
  const { t } = useTranslation();
  const setTitle = usePageTitleStore((state) => state.setTitle);

  const { data: unseenCount } = useUnseenMentionsCountQuery();
  const { mutate: markMentionsSeen } = useMarkMentionsSeen();
  const { data: mentionsCountData } = useMentionsQuery({ per_page: 1 });
  const { data: creatorsCountData } = useSearchCreatorsQuery({ page: 1, perPage: 1 });

  useEffect(() => {
    setTitle(t('mentions.title'));
    return () => setTitle(null);
  }, [setTitle, t]);

  useEffect(() => {
    if (unseenCount && unseenCount > 0) {
      markMentionsSeen();
    }
  }, [unseenCount, markMentionsSeen]);

  const mentionsCount = mentionsCountData?.pages[0]?.meta.total;
  const creatorsCount = creatorsCountData?.meta.total;
  const mentionsLabel =
    typeof mentionsCount === 'number'
      ? t('mentions.tabs.mentionsWithCount', { count: mentionsCount })
      : t('mentions.tabs.mentions');
  const creatorsLabel =
    typeof creatorsCount === 'number'
      ? t('mentions.tabs.creatorsWithCount', { count: creatorsCount })
      : t('mentions.tabs.creators');

  const tabs = [
    { value: 'mentions', label: mentionsLabel },
    { value: 'creators', label: creatorsLabel },
  ];

  return (
    <TopNavWithTabs tabs={tabs} defaultTab="mentions">
      <TabContent value="mentions">
        <PageHeader title={t('mentions.title')} description={t('mentions.description')} />
        <MentionsTabContent />
      </TabContent>

      <TabContent value="creators">
        <PageHeader title={t('creators.title')} description={t('creators.description')} />
        <CreatorsTable />
      </TabContent>
    </TopNavWithTabs>
  );
};

const MentionsTabContent = () => {
  // Read URL state for filters - using namespaced parameters
  const [platform] = useQueryState('mentionPlatform', parseAsString);
  const [mentionType] = useQueryState('mentionType', parseAsString);
  const [creatorIdsParam] = useQueryState('creatorIds', parseAsString);
  const [startDate] = useQueryState('mentionStartDate', parseAsString);
  const [endDate] = useQueryState('mentionEndDate', parseAsString);

  const creatorIds = creatorIdsParam ? creatorIdsParam.split(',').filter(Boolean) : undefined;

  // Build filters for the query
  const filters = {
    platform: platform || undefined,
    type: (mentionType as 'story' | 'post' | 'reel_caption') || undefined,
    creatorIds,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  };

  const {
    mentions,
    total,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useFlattenedMentions(filters);

  // Handle load more
  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  useMentionNavigatorSync({
    contextKey: 'mentions-page',
    mentions,
    total,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  // Determine if filters are active
  const hasActiveFilters = Boolean(
    platform || mentionType || creatorIdsParam || startDate || endDate
  );

  return (
    <>
      <MentionsFilters />
      <MentionsGrid
        mentions={mentions}
        total={total}
        isLoading={isLoading}
        isError={isError}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        onLoadMore={handleLoadMore}
        onRetry={() => refetch()}
        hasActiveFilters={hasActiveFilters}
        showCreator={true}
      />
    </>
  );
};

export default MentionsPage;
